import React, { useCallback, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router";

import {
  FILTER_LABEL,
  pathConstant,
  loginConstants,
  averageCardTitleConstants,
  emptyString,
  brandProfileConstants,
  brandPerformanceConstants,
  PERFORMANCE_STATUS_LABELS,
} from "../../constants";
import { setItemToStorage, getItemFromStorage } from "../../../../system";
import {
  useSpinner,
  Spinner,
  DataTable,
  StatusCard,
  ExportButton,
  SearchComponent,
  ChangeComponent,
  AverageRatingCard,
  calculateChangeColumnValueForSentimentRating,
  calculateChangeColumnValueForTransactionRating,
} from "../../../../shared";
import { createGroupPerformanceColumns } from "./utility";
import { getStarResponsesStatusTitle } from "../shared/performanceQueryUtility";

const {
  GET_RECORD_PAGINATION_RESPONSES,
  GET_AVERAGE_RATING_RESPONSES,
  ALL_RESTAURANT_RESPONSE_DATA,
  ALL_GROUP_RESPONSE_DATA,
} = brandPerformanceConstants;
const { AVERAGE_TRANSACTION_RATING, AVERAGE_SENTIMENT_RATING } =
  averageCardTitleConstants;

const GroupPerformance = (props) => {
  const {
    getData,
    sorting,
    dayCounts,
    exportFunc,
    searchString,
    setSearchString,
    selectedOption,
    totalResponses,
    totalRestaurants,
    userResponsesData,
    userResponseDataPagination,
    avgSentimentAndTransactionRatingData,
  } = props;

  //Getting brand id from storage
  const brandId = getItemFromStorage(loginConstants.BRAND_ID);

  const location = useLocation();

  useEffect(() => {
    if (userResponsesData && userResponsesData.length > 0) {
      calculateChangeColumnValueForSentimentRating(userResponsesData);
      calculateChangeColumnValueForTransactionRating(userResponsesData);
    }
    // eslint-disable-next-line
  }, [userResponsesData]);

  // Show loading while a request is running.
  const spinner = useSpinner();

  const getNavigateToPath = (storeId) => {
    switch (location.state?.name) {
      case pathConstant.BRAND_PROFILE:
      case pathConstant.STORE_BRAND_PROFILE:
        return `/stores/brand-profile/${brandId}/store-performance/${storeId}`;
      case pathConstant.GROUP:
        return `/stores/brand-groups/${brandId}/store-performance/${storeId}`;
      case pathConstant.PERFORMANCE:
      case pathConstant.STORE_PERFORMANCE:
        return `/stores/dashboard/${brandId}/brand-performance/store-performance/${storeId}`;
      default:
        return emptyString;
    }
  };

  //Handle click store name
  const handleClickStoreName = (profile) => {
    setItemToStorage(loginConstants.LOCATION_ID, profile.id);
    setItemToStorage(
      brandProfileConstants.LOCATION_STATE,
      JSON.stringify(location.state),
    );
  };

  const cellNameRenderer = useCallback(
    ({ row }) => {
      const profile = row.original;

      const navigateToPath = getNavigateToPath(profile.id);

      return profile.name ? (
        <Link
          className={`table-links`}
          to={navigateToPath}
          onClick={() => handleClickStoreName(profile)}
          state={location.state}
        >
          {profile.name}
        </Link>
      ) : (
        <></>
      );
    },
    // eslint-disable-next-line
    [location],
  );

  const headerTransactionRatingRenderer = useCallback(
    () => {
      return <span className="text-secondary">AVERAGE TRANSACTION RATING</span>;
    },
    // eslint-disable-next-line
    [],
  );

  const headerSurveyCompletionDateRenderer = useCallback(
    () => {
      return (
        <span className="text-secondary">
          PREVIOUS {dayCounts === 1 ? `${dayCounts} DAY` : `${dayCounts} DAYS`}
        </span>
      );
    },
    // eslint-disable-next-line
    [dayCounts],
  );

  const headerSentimentRatingRenderer = useCallback(
    () => {
      return <span className="text-secondary">AVERAGE SENTIMENT RATING</span>;
    },
    // eslint-disable-next-line
    [],
  );

  const cellTransactionChangeRenderer = useCallback(
    ({ row }) => {
      const {
        transactionChange,
        isTransactionChangeEqualToZero,
        isTransactionChangeGreaterThanZero,
      } = row.original;
      return (
        <ChangeComponent
          result={transactionChange}
          isValueEqualToZero={isTransactionChangeEqualToZero}
          isValueGreaterThanZero={isTransactionChangeGreaterThanZero}
        />
      );
    },
    // eslint-disable-next-line
    [],
  );

  const cellSentimentChangeRenderer = useCallback(
    ({ row }) => {
      const {
        sentimentChange,
        isSentimentChangeEqualToZero,
        isSentimentChangeGreaterThanZero,
      } = row.original;
      return (
        <ChangeComponent
          result={sentimentChange}
          isValueEqualToZero={isSentimentChangeEqualToZero}
          isValueGreaterThanZero={isSentimentChangeGreaterThanZero}
        />
      );
    },
    // eslint-disable-next-line
    [],
  );

  //Static headers for data table
  const columns = useMemo(
    () =>
      createGroupPerformanceColumns({
        cellNameRenderer,
        headerTransactionRatingRenderer,
        headerSurveyCompletionDateRenderer,
        cellTransactionChangeRenderer,
        headerSentimentRatingRenderer,
        cellSentimentChangeRenderer,
      }),
    [
      cellNameRenderer,
      headerTransactionRatingRenderer,
      headerSurveyCompletionDateRenderer,
      cellTransactionChangeRenderer,
      headerSentimentRatingRenderer,
      cellSentimentChangeRenderer,
    ],
  );

  const responsesStatusTitle = getStarResponsesStatusTitle(selectedOption, {
    allLabel: FILTER_LABEL.ALL,
    allResponsesLabel: PERFORMANCE_STATUS_LABELS.TOTAL_RESPONSES,
    buildFilteredLabel: (options) =>
      `Total ${options.map((item) => item.value)} Star Responses`,
  });

  return (
    <>
      {(spinner(ALL_RESTAURANT_RESPONSE_DATA) ||
        spinner(ALL_GROUP_RESPONSE_DATA) ||
        spinner(GET_RECORD_PAGINATION_RESPONSES) ||
        spinner(GET_AVERAGE_RATING_RESPONSES)) && <Spinner />}

      <div className="container-fluid py-3">
        <div className=" d-md-flex mb-3 justify-content-between pt-0">
          <StatusCard
            title={PERFORMANCE_STATUS_LABELS.TOTAL_STORES}
            data={totalRestaurants}
            classNames="me-3 mb-md-0 mb-3"
            bodyClassNames="p-3"
          />
          <StatusCard
            title={responsesStatusTitle}
            data={totalResponses}
            classNames=""
            bodyClassNames="p-3"
          />
        </div>
        <div className=" d-md-flex mb-3 justify-content-between mt-2">
          <AverageRatingCard
            dayCounts={dayCounts}
            bodyClassNames="px-3 pb-0"
            classNames="me-3 mb-3 mb-md-0"
            title={AVERAGE_TRANSACTION_RATING}
            averageRating={
              avgSentimentAndTransactionRatingData?.averageTransactionRatings
            }
            previousAverageRating={
              avgSentimentAndTransactionRatingData?.previousAverageTransactionRatings
            }
            averageRatingQuestionsData={
              avgSentimentAndTransactionRatingData?.averageTransaction
            }
          />
          <AverageRatingCard
            dayCounts={dayCounts}
            bodyClassNames="px-3 pb-0"
            classNames="mb-md-0 mb-3"
            title={AVERAGE_SENTIMENT_RATING}
            averageRating={
              avgSentimentAndTransactionRatingData?.averageSentimentRatings
            }
            previousAverageRating={
              avgSentimentAndTransactionRatingData?.previousAverageSentimentRatings
            }
            averageRatingQuestionsData={
              avgSentimentAndTransactionRatingData?.averageSentiment
                ? avgSentimentAndTransactionRatingData.averageSentiment
                : []
            }
          />
        </div>
        <ExportButton
          exportFunc={exportFunc}
          isDisabled={!userResponseDataPagination.totalElements}
        />
        <div className="notification-form card shadow">
          <div className="card-body px-0">
            <div className="d-flex justify-content-between">
              <h5 className=" responses px-4">
                {PERFORMANCE_STATUS_LABELS.TOTAL_STORES}
              </h5>
              <SearchComponent
                searchString={searchString}
                setSearchString={setSearchString}
              />
            </div>
            <div className="mt-3">
              <DataTable
                columns={columns}
                sortedBy={sorting}
                data={userResponsesData}
                onPageChange={(pageNum) =>
                  getData(pageNum, null, userResponseDataPagination.size)
                }
                onColumnSort={(sortBy) =>
                  getData(
                    userResponseDataPagination.number,
                    sortBy,
                    userResponseDataPagination.size,
                  )
                }
                onSizeChange={(size, pageNum) => {
                  getData(pageNum, null, size);
                }}
                pageSize={userResponseDataPagination.size}
                pageNumber={userResponseDataPagination.number + 1}
                totalRows={userResponseDataPagination.totalElements}
                numberOfRowsPerPage={userResponseDataPagination.size}
                isPaginationDropDownEnable={true}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GroupPerformance;
